import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader, PageLoader, EmptyState } from '@/components/common/Layout';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { RiskBadge } from '@/components/common/RiskBadge';
import { CategoryBadge } from '@/components/common/CategoryBadge';
import { ObservationStageBadge } from '@/components/common/ObservationStageBadge';
import { EvidenceBadge } from '@/components/common/EvidenceBadge';
import { SimulationDisclaimer } from '@/components/common/SimulationDisclaimer';
import { VerifiedBadge } from '@/components/common/VerifiedBadge';
import { api } from '@/lib/api';
import { fmtRel, fmtPct } from '@/lib/format';
import usePageTitle from '@/hooks/usePageTitle';

export default function DetectionsPage() {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState('all');
  usePageTitle('AI Observations');

  useEffect(() => { api.get('/detections', { params: { limit: 200 } }).then(r => setItems(r.data)); }, []);

  const filtered = (items || []).filter(d => filter === 'all' || d.validation_status === filter);

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      <PageHeader title="AI observations" subtitle="Satellite-derived change observations awaiting analyst review. Confidence is the AI's certainty in its interpretation, not the probability an incident has occurred." />
      <Card className="p-3 mb-4 flex gap-2 flex-wrap">
        {['all', 'Pending', 'Confirmed', 'Likely', 'False positive', 'Needs review'].map(f => (
          <button key={f} onClick={() => setFilter(f)}
            data-testid={`detections-filter-${f}`}
            className={`text-xs rounded-md px-2 py-1 border ${filter === f ? 'bg-primary text-primary-foreground border-primary' : 'bg-secondary border-border hover:bg-secondary/70'}`}>{f}</button>
        ))}
      </Card>
      {!items ? <PageLoader /> : filtered.length === 0 ? <EmptyState title="No observations for this filter" hint="Try switching to another validation status." /> : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map(d => (
            <Link key={d.id} to={`/app/detections/${d.id}`} data-testid={`detection-card-${d.id}`}>
              <Card className={`p-4 border-border hover:border-primary/40 h-full risk-stripe risk-stripe-${d.severity?.toLowerCase()}`}>
                <div className="flex items-center justify-between gap-2">
                  <CategoryBadge category={d.change_type} />
                  <RiskBadge severity={d.severity} />
                </div>
                <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                  {d.observation_stage && <ObservationStageBadge stage={d.observation_stage} />}
                  {d.is_simulation ? <SimulationDisclaimer compact /> : <VerifiedBadge />}
                </div>
                <div className="mt-3 text-sm line-clamp-3 min-h-[3rem]">{d.description}</div>
                <div className="mt-3 grid grid-cols-3 gap-2 text-[11px]">
                  <div><div className="text-muted-foreground">Area</div><div className="font-mono tabular-nums">{fmtPct(d.affected_area_pct)}</div></div>
                  <div><div className="text-muted-foreground">Confidence</div><div className="font-mono tabular-nums">{Math.round((d.llm_confidence || 0) * 100)}%</div></div>
                  <div><div className="text-muted-foreground">Risk</div><div className="font-mono tabular-nums">{d.risk_score}</div></div>
                </div>
                <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
                  {d.evidence_quality && <EvidenceBadge quality={d.evidence_quality} />}
                  <span className="text-[10px] text-muted-foreground">{fmtRel(d.created_at)}</span>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
